import { Component, inject, input, output, signal } from '@angular/core';
import { TeaserComponent } from '@app/components/teaser/teaser.component';
import { MobiUiCarouselComponent } from '@mobi/rwc-ui-components-ng-jslib';
import { BreakpointService } from '@app/services/breakpoint.service';
import { HeadingType, TeaserStyles, Url } from '@app/models/global.model';
import { ComponentSharedModule } from '@app/modules/component.shared.module';
import { EntitySharedModule } from '@app/modules/entity.shared.module';
import { Teaser } from '@app/models/entity.model';
import { TextsService } from '@app/services/texts.service';
import { DOCUMENT } from '@angular/common';
import { CalcUrlPipe } from '@app/pipes/calc-url.pipe';
import { ExtractUrlPipe } from '@app/pipes/extract-url.pipe';
import { getTrackingProductTaxonomy } from '@app/helpers/tracking-data-builders';

/** Prerollout click event data */
export interface PrerolloutClickEvent {
  id: string;
  prerolloutUrl: string;
  regularUrl: string;
  ctaTarget: string;
}

type TrackingPayload = Record<string, string>;
const TRACKING_FIELD_ALLOWLIST = [
  'page_contentphase',
  'page_segment',
  'page_subject',
  'product_name',
  'product_id',
] as const;

@Component({
  selector: 'mobi-teaser-list',
  templateUrl: './teaser-list.component.html',
  styleUrls: ['./teaser-list.component.scss'],
  imports: [
    ComponentSharedModule,
    TeaserComponent,
    MobiUiCarouselComponent,
    EntitySharedModule,
    CalcUrlPipe,
  ],
  providers: [ExtractUrlPipe, CalcUrlPipe],
  standalone: true,
})
export class TeaserListComponent {
  readonly teasers = input<Teaser[] | undefined>();
  readonly teaserStyle = input<TeaserStyles>('card');
  readonly isMobileSlider = input(true);
  readonly teaserWidth = input(true);
  readonly teaserTitleSize = input<HeadingType>('h4');
  readonly isCalcov = input(false);
  readonly inked = input(false);
  readonly overrideResponsiveStyle = input('');
  secondaryCtaLabel = signal('');
  readonly notCentered = input<boolean | undefined>();
  readonly centerRows = input(false);

  /** Emits when a teaser with prerollout is clicked (only in calcov mode) */
  readonly prerolloutClick = output<PrerolloutClickEvent>();

  breakpoint = inject(BreakpointService);
  #texts = inject(TextsService);
  #doc = inject(DOCUMENT);
  #extractUrl = inject(ExtractUrlPipe);
  #calcUrl = inject(CalcUrlPipe);

  constructor() {
    Promise.all([
      this.#texts.getText(
        'teaser_label_show_product',
        'frontend',
        'Mehr über das Produkt erfahren'
      ),
    ])
      .then((texts) => {
        this.secondaryCtaLabel.set(texts[0]);
      })
      .catch((e) => console.log(e));
  }

  /** Check if a teaser has prerollout configured */
  hasPrerollout(teaser: any): boolean {
    return Boolean(this.isCalcov() && teaser?.calculatorCtaPrer?.uri?.path && teaser?.id);
  }

  /** Handle primary CTA click for prerollout */
  onPrimaryCtaClick(event: MouseEvent, teaser: any): void {
    const regularUrl =
      this.#calcUrl.transform(
        this.#extractUrl.transform(teaser.url),
        this.#extractUrl.transform(teaser.calculatorCtaAuth)
      ) || '';

    this.prerolloutClick.emit({
      id: teaser.id,
      prerolloutUrl: teaser.calculatorCtaPrer?.uri?.path || '',
      regularUrl,
      ctaTarget: teaser.ctaTarget || '_self',
    });
  }

  isExternalUrl(url: string | undefined): string {
    if (url && url.startsWith('http')) {
      return new URL(url).origin !== this.#doc.location.origin ? '_blank' : '_self';
    } else {
      return '_self';
    }
  }

  getTrackingData(teaser: any): TrackingPayload | undefined {
    if (!teaser || !this.isCalcov()) return undefined;

    const payload: TrackingPayload = {};
    const put = (key: string, value: unknown) => {
      if (typeof value === 'string' && value.trim()) {
        payload[key] = value.trim();
      }
    };

    const productTaxonomy = getTrackingProductTaxonomy(teaser?.taxonomyProduct);
    put('product_name', productTaxonomy?.trackingName);
    put('product_id', productTaxonomy?.trackingId);

    const joinLabels = (items: any) =>
      Array.isArray(items)
        ? items
            .map((item: any) => item?.label)
            .filter(Boolean)
            .join(', ')
        : '';

    put('page_contentphase', joinLabels(teaser?.taxonomyContentphase));
    put('page_segment', joinLabels(teaser?.taxonomySegment));
    put('page_subject', joinLabels(teaser?.taxonomySubject));

    for (const key of TRACKING_FIELD_ALLOWLIST) {
      put(key, this.getTealiumAddvValue(teaser, key));
    }

    return Object.keys(payload).length ? payload : undefined;
  }

  private getTealiumAddvValue(teaser: any, key: string): string | undefined {
    if (!Array.isArray(teaser?.tealiumAddv)) return undefined;

    for (const entry of teaser.tealiumAddv) {
      if (!entry) continue;

      if (typeof entry?.key === 'string' && entry.key === key) {
        return typeof entry?.value === 'string' ? entry.value : undefined;
      }

      if (typeof entry?.[key] === 'string') {
        return entry[key];
      }
    }

    return undefined;
  }

  resolveTeaserTarget(teaser: Teaser): string {
    const teaserTarget = teaser?.url?.options?.attributes?.target;
    if (teaserTarget) {
      return teaserTarget;
    }

    const teaserUrl = this.#extractUrl.transform(teaser?.url);
    if (teaserUrl) {
      return this.isExternalUrl(teaserUrl);
    }

    const calculatorTarget = teaser?.calculatorCta?.options?.attributes?.target;
    if (calculatorTarget) {
      return calculatorTarget;
    }

    return this.isExternalUrl(this.#extractUrl.transform(teaser?.calculatorCta));
  }
}
